/**
 * BHPH Late Fee Management Page
 * Configure late fee rules, preview eligible accounts, view history, and manage waivers.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  applyLateFee,
  formatCurrency,
  getEligibleAccountsForLateFees,
  getLateFeeConfig,
  getLateFeeStats,
  updateLateFeeConfig,
  type LateFeeConfig,
} from '../../services/bhphApi';

// ===== Form Data Types =====
interface LateFeeConfigFormData {
  late_fee_amount: string;
  late_fee_percentage: string;
  use_percentage: boolean;
  grace_period_days: string;
  max_late_fees_per_period: string;
  min_balance_for_fee: string;
  max_fee_amount: string;
  enabled: boolean;
  apply_to_statuses: string;
  send_notification: boolean;
}

// ===== Main Component =====
const LateFeeManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'config' | 'eligible' | 'stats'>('config');

  // Fetch late fee configuration
  const { data: config, isLoading: loadingConfig } = useQuery({
    queryKey: ['lateFeeConfig'],
    queryFn: getLateFeeConfig,
  });

  // Fetch eligible accounts
  const { data: eligibleAccounts, isLoading: loadingEligible } = useQuery({
    queryKey: ['eligibleLateFees'],
    queryFn: getEligibleAccountsForLateFees,
    enabled: activeTab === 'eligible',
  });

  // Fetch statistics
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['lateFeeStats'],
    queryFn: getLateFeeStats,
    enabled: activeTab === 'stats',
  });

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Late Fee Management</h1>
        <p className="text-gray-600 mt-1">
          Configure rules, preview eligible accounts, and manage late fees
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        {/* Tab Headers */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <TabButton
              label="Configuration"
              icon="⚙️"
              active={activeTab === 'config'}
              onClick={() => setActiveTab('config')}
            />
            <TabButton
              label="Eligible Accounts"
              icon="👥"
              active={activeTab === 'eligible'}
              onClick={() => setActiveTab('eligible')}
            />
            <TabButton
              label="Statistics"
              icon="📊"
              active={activeTab === 'stats'}
              onClick={() => setActiveTab('stats')}
            />
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'config' && <ConfigurationTab config={config} loading={loadingConfig} />}
          {activeTab === 'eligible' && (
            <EligibleAccountsTab accounts={eligibleAccounts} loading={loadingEligible} />
          )}
          {activeTab === 'stats' && <StatisticsTab stats={stats} loading={loadingStats} />}
        </div>
      </div>
    </div>
  );
};

// ===== Component: Tab Button =====
interface TabButtonProps {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({ label, icon, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 font-medium text-sm border-b-2 transition duration-150 flex items-center ${
        active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
      }`}
    >
      <span className="mr-2">{icon}</span>
      {label}
    </button>
  );
};

// ===== Tab: Configuration =====
interface ConfigurationTabProps {
  config?: LateFeeConfig;
  loading: boolean;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ config, loading }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, watch } = useForm<LateFeeConfigFormData>({
    defaultValues: config
      ? {
          late_fee_amount: config.late_fee_amount.toString(),
          late_fee_percentage: config.late_fee_percentage.toString(),
          use_percentage: config.use_percentage,
          grace_period_days: config.grace_period_days.toString(),
          max_late_fees_per_period: config.max_late_fees_per_period.toString(),
          min_balance_for_fee: config.min_balance_for_fee.toString(),
          max_fee_amount: config.max_fee_amount?.toString() || '',
          enabled: config.enabled,
          apply_to_statuses: config.apply_to_statuses,
          send_notification: config.send_notification,
        }
      : undefined,
  });

  const usePercentage = watch('use_percentage');
  const enabled = watch('enabled');

  const updateConfigMutation = useMutation({
    mutationFn: updateLateFeeConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lateFeeConfig'] });
      toast.success('Late fee configuration updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Error updating configuration: ${error.response?.data?.detail || error.message}`);
    },
  });

  const onSubmit = (data: LateFeeConfigFormData) => {
    if (!confirm('Are you sure you want to update the late fee configuration?')) {
      return;
    }

    updateConfigMutation.mutate({
      late_fee_amount: parseFloat(data.late_fee_amount),
      late_fee_percentage: parseFloat(data.late_fee_percentage),
      use_percentage: data.use_percentage,
      grace_period_days: parseInt(data.grace_period_days),
      max_late_fees_per_period: parseInt(data.max_late_fees_per_period),
      min_balance_for_fee: parseFloat(data.min_balance_for_fee),
      max_fee_amount: data.max_fee_amount ? parseFloat(data.max_fee_amount) : null,
      enabled: data.enabled,
      apply_to_statuses: data.apply_to_statuses,
      send_notification: data.send_notification,
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading configuration...</div>;
  }

  return (
    <div>
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <span className="text-2xl mr-3">ℹ️</span>
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Late Fee Configuration</h3>
            <p className="text-sm text-blue-800">
              Configure how late fees are calculated and applied to delinquent accounts. Changes
              take effect immediately for future fee calculations.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              {...register('enabled')}
              className="mr-3 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
            />
            <div>
              <span className="text-lg font-semibold text-gray-900">
                Late Fee System {enabled ? 'Enabled' : 'Disabled'}
              </span>
              <p className="text-sm text-gray-600">
                {enabled
                  ? 'Late fees will be automatically calculated and applied daily'
                  : 'Late fee automation is currently disabled'}
              </p>
            </div>
          </label>
        </div>

        {/* Fee Calculation Method */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Calculation</h3>

            <div className="space-y-4">
              {/* Calculation Method Toggle */}
              <div>
                <label className="flex items-center">
                  <input type="checkbox" {...register('use_percentage')} className="mr-2" />
                  <span className="text-sm font-medium text-gray-700">
                    Use percentage instead of fixed amount
                  </span>
                </label>
              </div>

              {/* Fixed Amount */}
              {!usePercentage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fixed Late Fee Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('late_fee_amount', { required: !usePercentage })}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Flat fee charged for each late payment
                  </p>
                </div>
              )}

              {/* Percentage */}
              {usePercentage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Fee Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      {...register('late_fee_percentage', { required: usePercentage })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Percentage of payment amount (e.g., 5% of monthly payment)
                  </p>
                </div>
              )}

              {/* Max Fee Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Fee Amount (Optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('max_fee_amount')}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="No limit"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Cap the maximum late fee (leave blank for no limit)
                </p>
              </div>
            </div>
          </div>

          {/* Fee Rules */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Rules</h3>

            <div className="space-y-4">
              {/* Grace Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grace Period (Days) *
                </label>
                <input
                  type="number"
                  min="0"
                  {...register('grace_period_days', { required: true })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Days after due date before late fee is applied
                </p>
              </div>

              {/* Max Fees Per Period */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Fees Per Period *
                </label>
                <input
                  type="number"
                  min="1"
                  {...register('max_late_fees_per_period', { required: true })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-600 mt-1">Maximum late fees applied per month</p>
              </div>

              {/* Minimum Balance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Balance for Fee *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('min_balance_for_fee', { required: true })}
                    className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Only apply fees if balance is above this amount
                </p>
              </div>

              {/* Apply To Statuses */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Apply to Account Statuses *
                </label>
                <input
                  type="text"
                  {...register('apply_to_statuses', { required: true })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="ACTIVE,PENDING"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Comma-separated list of statuses (e.g., ACTIVE,PENDING)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
          <div>
            <label className="flex items-center">
              <input type="checkbox" {...register('send_notification')} className="mr-2 h-4 w-4" />
              <span className="text-sm font-medium text-gray-700">
                Send notification to customer when late fee is applied
              </span>
            </label>
            <p className="text-xs text-gray-600 mt-1 ml-6">
              Customers will receive SMS/email notification about late fees
            </p>
          </div>
        </div>

        {/* Example Calculation */}
        {config && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Example Calculation</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Scenario:</strong> Account is {watch('grace_period_days')} days past due
                with a ${400} monthly payment
              </p>
              <p className="text-sm text-gray-700">
                <strong>Late Fee:</strong>{' '}
                {usePercentage
                  ? `$${(400 * (parseFloat(watch('late_fee_percentage') || '0') / 100)).toFixed(2)} (${watch('late_fee_percentage')}% of $400)`
                  : `$${parseFloat(watch('late_fee_amount') || '0').toFixed(2)} (fixed amount)`}
              </p>
              {watch('max_fee_amount') && (
                <p className="text-xs text-gray-600 mt-1">
                  * Capped at ${parseFloat(watch('max_fee_amount')).toFixed(2)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={updateConfigMutation.isPending}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-150 disabled:opacity-50"
          >
            {updateConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ===== Tab: Eligible Accounts =====
interface EligibleAccountsTabProps {
  accounts?: any[];
  loading: boolean;
}

const EligibleAccountsTab: React.FC<EligibleAccountsTabProps> = ({ accounts, loading }) => {
  const queryClient = useQueryClient();
  const [processingAccountId, setProcessingAccountId] = useState<string | null>(null);

  const applyFeeMutation = useMutation({
    mutationFn: ({ accountId, notes }: { accountId: string; notes?: string }) =>
      applyLateFee(accountId, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eligibleLateFees'] });
      queryClient.invalidateQueries({ queryKey: ['lateFeeStats'] });
      queryClient.invalidateQueries({ queryKey: ['bhphAccount', variables.accountId] });
      setProcessingAccountId(null);
      toast.success('Late fee applied successfully');
    },
    onError: (error: any) => {
      toast.error(`Error applying late fee: ${error.response?.data?.detail || error.message}`);
      setProcessingAccountId(null);
    },
  });

  const handleApplyFee = (accountId: string) => {
    if (!confirm('Are you sure you want to manually apply a late fee to this account?')) {
      return;
    }
    setProcessingAccountId(accountId);
    applyFeeMutation.mutate({ accountId, notes: 'Manually applied' });
  };

  if (loading) {
    return <div className="text-center py-8">Loading eligible accounts...</div>;
  }

  if (!accounts || accounts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">✅</div>
        <p className="text-lg font-semibold text-gray-900 mb-2">No Eligible Accounts</p>
        <p className="text-gray-600">All accounts are current or within the grace period</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-2xl mr-3">⚠️</span>
            <div>
              <h3 className="font-semibold text-orange-900">
                {accounts.length} Account{accounts.length !== 1 ? 's' : ''} Eligible for Late Fees
              </h3>
              <p className="text-sm text-orange-800">
                These accounts are past the grace period and eligible for late fee assessment
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Potential Fees</p>
            <p className="text-2xl font-bold text-orange-700">
              {formatCurrency(accounts.reduce((sum, acc) => sum + (acc.calculated_fee || 0), 0))}
            </p>
          </div>
        </div>
      </div>

      {/* Eligible Accounts Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Account
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Days Past Due
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Balance
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Calculated Fee
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {accounts.map((account) => (
              <tr key={account.account_id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                  {account.account_id}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{account.buyer_name}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-700">
                    {account.days_past_due} days
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                  {formatCurrency(account.total_balance_amount)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-red-600">
                  {formatCurrency(account.calculated_fee || 0)}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleApplyFee(account.account_id)}
                    disabled={processingAccountId === account.account_id}
                    className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                  >
                    {processingAccountId === account.account_id ? 'Applying...' : 'Apply Fee'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===== Tab: Statistics =====
interface StatisticsTabProps {
  stats?: any;
  loading: boolean;
}

const StatisticsTab: React.FC<StatisticsTabProps> = ({ stats, loading }) => {
  if (loading) {
    return <div className="text-center py-8">Loading statistics...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8 text-gray-600">No statistics available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Fees Applied"
          value={stats.total_fees_applied.toString()}
          subtitle="Lifetime"
          color="blue"
        />
        <StatCard
          title="Total Amount Collected"
          value={formatCurrency(stats.total_amount_collected)}
          subtitle="Revenue"
          color="green"
        />
        <StatCard
          title="This Month"
          value={formatCurrency(stats.this_month_fees_amount)}
          subtitle={`${stats.this_month_fees_count} fees`}
          color="purple"
        />
        <StatCard
          title="Waiver Rate"
          value={`${stats.waiver_rate.toFixed(1)}%`}
          subtitle={`${stats.total_fees_waived} waived`}
          color="orange"
        />
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fee Breakdown */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Breakdown</h3>
          <div className="space-y-3">
            <StatRow label="Total Fees Applied" value={stats.total_fees_applied} />
            <StatRow
              label="Active Fees"
              value={stats.total_fees_active}
              highlight="text-blue-600"
            />
            <StatRow
              label="Fees Waived"
              value={stats.total_fees_waived}
              highlight="text-gray-600"
            />
            <div className="pt-3 border-t border-gray-200">
              <StatRow
                label="Waiver Rate"
                value={`${stats.waiver_rate.toFixed(1)}%`}
                highlight="text-orange-600 font-semibold"
              />
            </div>
          </div>
        </div>

        {/* Revenue Tracking */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Tracking</h3>
          <div className="space-y-3">
            <StatRow
              label="Total Amount Applied"
              value={formatCurrency(stats.total_amount_applied)}
            />
            <StatRow
              label="Total Amount Collected"
              value={formatCurrency(stats.total_amount_collected)}
              highlight="text-green-600"
            />
            <StatRow
              label="Total Amount Waived"
              value={formatCurrency(stats.total_amount_waived)}
              highlight="text-gray-600"
            />
            <div className="pt-3 border-t border-gray-200">
              <StatRow
                label="This Month Revenue"
                value={formatCurrency(stats.this_month_fees_amount)}
                highlight="text-purple-600 font-semibold"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Average Fee</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.total_fees_applied > 0
                ? formatCurrency(stats.total_amount_applied / stats.total_fees_applied)
                : '$0.00'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Collection Rate</p>
            <p className="text-2xl font-bold text-green-600">
              {stats.total_amount_applied > 0
                ? `${((stats.total_amount_collected / stats.total_amount_applied) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Fees This Month</p>
            <p className="text-2xl font-bold text-purple-600">{stats.this_month_fees_count}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Component: Stat Card =====
interface StatCardProps {
  title: string;
  value: string;
  subtitle: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-lg p-4`}>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs mt-1">{subtitle}</p>
    </div>
  );
};

// ===== Component: Stat Row =====
interface StatRowProps {
  label: string;
  value: string | number;
  highlight?: string;
}

const StatRow: React.FC<StatRowProps> = ({ label, value, highlight = 'text-gray-900' }) => {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}:</span>
      <span className={highlight}>{value}</span>
    </div>
  );
};

export default LateFeeManagementPage;
